import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AlertCircle, ArrowLeft, ClipboardList, Plus, Trash2 } from 'lucide-react'
import { apiJson } from '../api'
import { hasAnyRole } from '../auth'

interface QuestionForm {
  key: number
  text: string
  options: string[]
  correctIndex: number
  points: number
}

interface ManageQuiz {
  id: string
  title: string
  passScore: number
  questions: {
    id: string
    text: string
    options: string[]
    correctIndex: number
    points: number
  }[]
}

let keySeq = 1
const newQuestion = (): QuestionForm => ({
  key: keySeq++,
  text: '',
  options: ['', ''],
  correctIndex: 0,
  points: 1,
})

const inputCls =
  'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100'
const labelCls = 'mb-1.5 block text-xs font-semibold text-slate-500'

export default function QuizForm() {
  const { id: courseId } = useParams()
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')

  const [title, setTitle] = useState('')
  const [passScore, setPassScore] = useState('70')
  const [questions, setQuestions] = useState<QuestionForm[]>([newQuestion()])
  const [exists, setExists] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const q = await apiJson<ManageQuiz>(`/courses/${courseId}/quiz/manage`)
      setTitle(q.title)
      setPassScore(String(q.passScore))
      setQuestions(
        q.questions.map((x) => ({
          key: keySeq++,
          text: x.text,
          options: x.options,
          correctIndex: x.correctIndex,
          points: x.points,
        })),
      )
      setExists(true)
    } catch {
      // 404 -> no quiz yet; keep the blank editor
    } finally {
      setLoading(false)
    }
  }, [courseId])

  useEffect(() => {
    load()
  }, [load])

  const updateQuestion = (key: number, patch: Partial<QuestionForm>) =>
    setQuestions((qs) => qs.map((q) => (q.key === key ? { ...q, ...patch } : q)))

  const updateOption = (key: number, oi: number, value: string) =>
    setQuestions((qs) =>
      qs.map((q) =>
        q.key === key ? { ...q, options: q.options.map((o, i) => (i === oi ? value : o)) } : q,
      ),
    )

  const addOption = (key: number) =>
    setQuestions((qs) =>
      qs.map((q) => (q.key === key && q.options.length < 6 ? { ...q, options: [...q.options, ''] } : q)),
    )

  const removeOption = (key: number, oi: number) =>
    setQuestions((qs) =>
      qs.map((q) => {
        if (q.key !== key || q.options.length <= 2) return q
        const options = q.options.filter((_, i) => i !== oi)
        const correctIndex = q.correctIndex >= options.length ? 0 : q.correctIndex === oi ? 0 : q.correctIndex > oi ? q.correctIndex - 1 : q.correctIndex
        return { ...q, options, correctIndex }
      }),
    )

  const valid =
    title.trim().length > 0 &&
    questions.length > 0 &&
    questions.every(
      (q) => q.text.trim().length > 0 && q.options.every((o) => o.trim().length > 0),
    )

  const save = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = {
        title: title.trim(),
        passScore: parseInt(passScore, 10) || 70,
        questions: questions.map((q) => ({
          text: q.text.trim(),
          options: q.options.map((o) => o.trim()),
          correctIndex: q.correctIndex,
          points: q.points,
        })),
      }
      await apiJson(`/courses/${courseId}/quiz`, {
        method: 'PUT',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
      })
      navigate(`/courses/${courseId}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!window.confirm('Delete this quiz? Attempt history is kept, but learners can then complete the course without it.')) return
    setDeleting(true)
    setError('')
    try {
      await apiJson(`/courses/${courseId}/quiz`, { method: 'DELETE' })
      navigate(`/courses/${courseId}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed')
      setDeleting(false)
    }
  }

  if (!allowManage) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-700">
        <AlertCircle size={16} className="shrink-0" />
        Only HR or admin users can manage quizzes.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-brand-600">
        <ArrowLeft size={15} /> Back to course
      </Link>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center gap-3.5 border-b border-slate-100 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white shadow-md shadow-brand-500/25">
            <ClipboardList size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-bold tracking-tight">{exists ? 'Edit Quiz' : 'New Quiz'}</h1>
            <p className="text-xs text-slate-400">Learners must pass this quiz to complete the course</p>
          </div>
          {exists && (
            <button
              onClick={remove}
              disabled={deleting}
              className="rounded-xl border border-red-200 px-3.5 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
            >
              {deleting ? 'Deleting…' : 'Delete quiz'}
            </button>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save()
          }}
          className="space-y-5 px-6 py-6"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className={labelCls}>Quiz title *</label>
              <input required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Safety fundamentals assessment" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Pass score (%)</label>
              <input type="number" min={1} max={100} value={passScore} onChange={(e) => setPassScore(e.target.value)} className={inputCls} />
            </div>
          </div>

          {questions.map((q, qi) => (
            <div key={q.key} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Question {qi + 1}</p>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                    Points
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={q.points}
                      onChange={(e) => updateQuestion(q.key, { points: parseInt(e.target.value, 10) || 1 })}
                      className="w-14 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs outline-none focus:border-brand-400"
                    />
                  </label>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setQuestions((qs) => qs.filter((x) => x.key !== q.key))}
                      className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      title="Remove question"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
              <textarea
                rows={2}
                maxLength={2000}
                value={q.text}
                onChange={(e) => updateQuestion(q.key, { text: e.target.value })}
                placeholder="Question text…"
                className={`${inputCls} mt-2 resize-y bg-white`}
              />
              <div className="mt-3 space-y-2">
                {q.options.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2.5">
                    <input
                      type="radio"
                      name={`correct-${q.key}`}
                      checked={q.correctIndex === oi}
                      onChange={() => updateQuestion(q.key, { correctIndex: oi })}
                      title="Mark as the correct answer"
                      className="h-4 w-4 shrink-0 border-slate-300 text-emerald-600 focus:ring-emerald-400"
                    />
                    <input
                      maxLength={500}
                      value={opt}
                      onChange={(e) => updateOption(q.key, oi, e.target.value)}
                      placeholder={`Option ${oi + 1}`}
                      className={`${inputCls} bg-white py-2`}
                    />
                    {q.options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeOption(q.key, oi)}
                        className="shrink-0 rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
                        title="Remove option"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2.5 flex items-center justify-between">
                <p className="text-[11px] text-slate-400">Select the radio next to the correct answer</p>
                {q.options.length < 6 && (
                  <button
                    type="button"
                    onClick={() => addOption(q.key)}
                    className="text-xs font-semibold text-brand-600 hover:text-brand-700"
                  >
                    + Add option
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={() => setQuestions((qs) => [...qs, newQuestion()])}
            disabled={questions.length >= 50}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-500 transition-colors hover:border-brand-300 hover:text-brand-600"
          >
            <Plus size={15} /> Add question
          </button>

          {error && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <Link
              to={`/courses/${courseId}`}
              className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving || !valid}
              className="rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:brightness-110 disabled:opacity-50"
            >
              {saving ? 'Saving…' : exists ? 'Save quiz' : 'Create quiz'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
